"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { FigmaSectionTitle } from "./FigmaSectionTitle";
import { FigmaProductCarousel } from "./FigmaProductCarousel";
import { FigmaProductCard } from "./FigmaProductCard";
import { SocialFeedSection } from "./SocialFeedSection";
import { getManufacturerBrands } from "@/services/brandService";
import { getProducts } from "@/services/productService";
import { resolveAssetUrl } from "@/utils/media";
import { SafeImage } from "@/components/common/SafeImage";
import { logger } from "@/utils/logger";
import { figmaTrustItems } from "@/data/figmaHome";

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const sectionShell = (
  titleWidth,
  itemCount,
  itemClassName,
  itemKeyPrefix,
  cardClassName,
) => (
  <section className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32">
    <div className="space-y-5">
      <div className={`h-7 ${titleWidth} animate-pulse rounded-full bg-zinc-200/80`} />
      <div className={itemClassName}>
        {Array.from({ length: itemCount }).map((_, index) => (
          <div
            key={`${itemKeyPrefix}-${index}`}
            className={`${cardClassName} animate-pulse rounded-2xl bg-zinc-200/70`}
          />
        ))}
      </div>
    </div>
  </section>
);

const DeferredLoading = () => (
  <div className="space-y-10">
    {sectionShell("w-56", 5, "grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5", "deals-skeleton", "h-90")}
    <section className="w-full overflow-hidden bg-zinc-100/60 px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32 py-10">
      <div className="space-y-8">
        <div className="h-7 w-64 animate-pulse rounded-full bg-zinc-200/80" />
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={`brands-skeleton-${index}`}
              className="h-18 w-35 shrink-0 animate-pulse rounded-xl bg-zinc-200/70"
            />
          ))}
        </div>
      </div>
    </section>
    {sectionShell("w-60", 5, "grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5", "popular-skeleton", "h-95")}
    {sectionShell("w-72", 6, "grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6", "manufacturer-skeleton", "h-24")}
    {sectionShell("w-56", 5, "grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5", "new-arrivals-skeleton", "h-95")}
  </div>
);

export const HomeDeferredSections = () => {
  const [brands, setBrands] = useState([]);
  const [dealProducts, setDealProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [popularProducts, setPopularProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let idleId = null;
    let timeoutId = null;

    const run = async () => {
      try {
        const [brandsRes, dealsRes, newArrivalsRes, popularRes] = await Promise.allSettled([
          getManufacturerBrands({ limit: 24 }),
          getProducts({ page: 1, limit: 8, isOnSale: true }),
          getProducts({ page: 1, limit: 8, sort: "newest" }),
          getProducts({ page: 1, limit: 8, isFeatured: true }),
        ]);

        if (cancelled) return;

        const brandsPayload = brandsRes.status === "fulfilled" ? brandsRes.value || {} : {};
        const normalizedBrands = Array.isArray(brandsPayload)
          ? brandsPayload
          : brandsPayload.brands || [];

        setBrands(normalizedBrands);
        setDealProducts(normalizeArray(dealsRes.status === "fulfilled" ? dealsRes.value : []));
        setNewArrivals(normalizeArray(newArrivalsRes.status === "fulfilled" ? newArrivalsRes.value : []));
        setPopularProducts(normalizeArray(popularRes.status === "fulfilled" ? popularRes.value : []));
      } catch (error) {
        logger.error("[FIGMA_HOME] Failed to load deferred homepage content:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const schedule = () => {
      void run();
    };

    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(schedule, { timeout: 7000 });
    } else {
      timeoutId = window.setTimeout(schedule, 3200);
    }

    return () => {
      cancelled = true;
      if (idleId !== null && typeof window !== "undefined" && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  const brandImageHref = (brand) => brand?.imageUrl || brand?.image || "";
  const featuredBrands = useMemo(() => brands.slice(0, 8), [brands]);
  const marqueeBrands = useMemo(
    () => Array.from({ length: 4 }).flatMap(() => featuredBrands),
    [featuredBrands],
  );
  const manufacturerBrands = useMemo(() => brands.slice(0, 12), [brands]);
  const popularTopProducts = useMemo(() => popularProducts.slice(0, 10), [popularProducts]);
  const newArrivalTopProducts = useMemo(() => newArrivals.slice(0, 5), [newArrivals]);

  if (loading) {
    return <DeferredLoading />;
  }

  return (
    <div className="space-y-10">
      {dealProducts.length > 0 ? (
        <section
          className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32"
          style={{ contentVisibility: "auto", containIntrinsicSize: "460px" }}
        >
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-4">
              <FigmaSectionTitle
                title="Deals of the Day"
                subtitle="Don't miss out on today's best deals on genuine parts."
              />
              <div className="hidden items-center gap-2 sm:flex">
                <span className="rounded-full bg-amber-500 px-3 py-1 text-xs font-bold uppercase tracking-wider text-zinc-950 glow-amber">
                  Live Deals
                </span>
              </div>
            </div>
            <FigmaProductCarousel products={dealProducts} limit={6} />
          </div>
        </section>
      ) : null}

      {brands.length > 0 ? (
        <section
          className="w-full overflow-hidden bg-zinc-100/60 px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32 py-10"
          style={{ contentVisibility: "auto", containIntrinsicSize: "360px" }}
        >
          <div className="space-y-8">
            <FigmaSectionTitle
              title="Shop by Vehicle Brand"
              subtitle="Explore our catalog across premium auto manufacturers."
              align="left"
            />
            <div
              className="relative mx-auto flex w-full max-w-[95%] overflow-hidden whitespace-nowrap py-4"
              style={{
                maskImage:
                  "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
              }}
            >
              <div
                className="flex w-max animate-marquee items-center gap-6 pr-6"
                style={{ animationDuration: "140s", animationTimingFunction: "linear" }}
              >
                {marqueeBrands.map((brand, idx) => {
                    const brandImage = brandImageHref(brand);
                    return (
                      <Link
                        key={`mq-${brand._id || brand.id || brand.name}-${idx}`}
                        href={`/products?manufacturerBrand=${encodeURIComponent(brand.name || "")}`}
                        className="group relative inline-flex h-18 w-35 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-400/70 hover:shadow-[0_8px_24px_rgba(24,24,27,0.10)]"
                        title={brand.name}
                      >
                        {brandImage ? (
                          <Image
                            src={resolveAssetUrl(brandImage)}
                            alt={brand.name}
                            fill
                            className="object-contain p-3.5 grayscale transition duration-300 group-hover:grayscale-0"
                            sizes="140px"
                          />
                        ) : (
                          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                            {brand.name}
                          </span>
                        )}
                      </Link>
                    );
                  })}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {popularProducts.length > 0 ? (
        <section
          className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32"
          style={{ contentVisibility: "auto", containIntrinsicSize: "520px" }}
        >
          <div className="space-y-5">
            <FigmaSectionTitle
              title="Popular Parts & Components"
              subtitle="High-demand auto parts trusted by thousands of customers."
            />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {popularTopProducts.map((product) => (
                <div key={product._id || product.id || product.slug} className="w-full">
                  <FigmaProductCard product={product} />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section
        className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32"
        style={{ contentVisibility: "auto", containIntrinsicSize: "420px" }}
      >
        <div className="space-y-5">
          <FigmaSectionTitle
            title="Shop by Manufacturer"
            subtitle="Direct from top-tier OEMs and aftermarket leaders."
          />
          {brands.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {manufacturerBrands.map((brand) => {
                const brandImage = brandImageHref(brand);
                return (
                  <Link
                    key={`m-${brand._id || brand.id || brand.name}`}
                    href={`/products?manufacturerBrand=${encodeURIComponent(brand.name || "")}`}
                    className="group flex min-h-15 flex-col items-center justify-center gap-3 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:border-amber-400/70 hover:shadow-md"
                  >
                    {brandImage ? (
                      <div className="relative h-12 w-24">
                        <Image
                          src={resolveAssetUrl(brandImage)}
                          alt={brand.name}
                          fill
                          className="object-contain grayscale transition duration-300 group-hover:grayscale-0"
                          sizes="100px"
                        />
                      </div>
                    ) : (
                      <span className="text-sm font-bold text-zinc-700">{brand.name}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          ) : null}
        </div>
      </section>

      {newArrivals.length > 0 ? (
        <section
          className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32"
          style={{ contentVisibility: "auto", containIntrinsicSize: "480px" }}
        >
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-4">
              <FigmaSectionTitle
                title="New Arrivals"
                subtitle="The latest additions to our genuine parts catalog."
              />
              <Link
                href="/products?sort=newest"
                className="flex items-center gap-1 rounded-full border border-amber-300/70 bg-amber-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-700 transition-all hover:bg-amber-100 hover:text-amber-800"
              >
                View All
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {newArrivalTopProducts.map((product) => (
                <div key={product._id || product.id || product.slug} className="w-full">
                  <FigmaProductCard product={product} />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <SocialFeedSection />
    </div>
  );
};
